// Shared by the hotline components: both the registration form and the edit dialog surface raw
// API failures to an operator, and both need the same unwrapping.
/**
 * Turns whatever the API threw into something an operator can read.
 *
 * This used to be `'…try again.' + e.error`, which printed a literal `[object Object]` whenever the
 * body was not a plain string. Both of our error shapes are objects: a bodiless 500 comes back as
 * RFC-9110 ProblemDetails (`{ title, status, traceId }`), and only `BadRequest`/`NotFound` send a
 * bare string. So the operator saw `[object Object]` on exactly the failures worth explaining.
 *
 * The traceId is included when present — it is the only handle on a server-side 500, and asking
 * an operator to reproduce a failure just to recover it wastes everyone's time.
 */
export function describeHttpError(e: unknown): string {
  const body = (e as { error?: unknown })?.error;

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  if (body && typeof body === 'object') {
    const details = body as { title?: string; detail?: string; message?: string; traceId?: string };
    const text = [details.detail, details.title, details.message].find(v => !!v?.trim());
    if (text) {
      return details.traceId ? `${text.trim()} (Ref: ${details.traceId})` : text.trim();
    }
    if (details.traceId) {
      return `The server reported an unexpected error. (Ref: ${details.traceId})`;
    }
  }

  const message = (e as { message?: string })?.message;
  return message?.trim() || 'Please check the details and try again.';
}
